import { useMutation } from '@tanstack/react-query';

import { getConfirmBookingDetailsById } from 'reservations/base/constants/endpoints';
import { HTTP_STATUS_CODE_403_UNAUTHORIZED } from 'reservations/base/constants/httpStatusCodes';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { BookingResponseSchema } from 'reservations/booking/types';
import type {
  HandleErrorOptions,
  HandleSuccessOptions,
  NextableBookingResponse
} from 'reservations/booking/types';
import { restClient } from 'utility/restClient';
import type { RequestError } from 'utility/restClient';

type UseUpdateBookingMutation = {
  slug: string;
  bookingId: string;
  apiKey: string;

  handleSuccess: (options: HandleSuccessOptions) => void;

  handleError: (options: HandleErrorOptions) => void;
};

function useConfirmBookingMutation({
  slug,
  bookingId,
  apiKey,
  handleSuccess,
  handleError
}: UseUpdateBookingMutation) {
  return useMutation<
    NextableBookingResponse,
    RequestError,
    Record<string, any>
  >({
    mutationFn: async () => {
      const endpoint = getConfirmBookingDetailsById(bookingId);
      const response = await restClient.put(endpoint, {
        data: {},
        nextableApiKey: apiKey
      });

      try {
        return BookingResponseSchema.parse(response);
      } catch (err) {
        console.error('useConfirmBookingMutation: ', err);
        throw err;
      }
    },
    onSuccess: data => {
      const { booking } = data;
      const { bookingId } = booking;

      handleSuccess({
        navUrl: `/locations/${slug}/booking/${bookingId}`
      });
    },
    onError: error => {
      if (error.code === HTTP_STATUS_CODE_403_UNAUTHORIZED) {
        handleError({
          forceReload: true
        });
        return;
      }

      if (error.code === NEXTABLE_ERROR_CODE.ITEM_NOT_FOUND) {
        handleError({
          navUrl: `/locations/${slug}`,
          code: error.code
        });
        return;
      }

      handleError({ navUrl: `/locations/${slug}/booking/${bookingId}` });
    }
  });
}

export { useConfirmBookingMutation };
